import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  Form, 
  Input, 
  Button, 
  DatePicker, 
  InputNumber, 
  Select, 
  Space, 
  Divider, 
  Checkbox,
  Typography,
  Alert
} from 'antd';
import { CalendarOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const MeetingDrawer = ({ 
  visible, 
  onClose, 
  onFinish, 
  editingMeeting = null,
  parentMeeting = null
}) => {
  const [form] = Form.useForm();
  const [addFollowUp, setAddFollowUp] = useState(false);
  const [followUpForm] = Form.useForm();

  // Reset form when drawer opens/closes or editing meeting changes
  useEffect(() => {
    if (visible) {
      if (editingMeeting) {
        form.setFieldsValue({
          ...editingMeeting,
          scheduledAt: editingMeeting.scheduledAt ? dayjs(editingMeeting.scheduledAt) : null,
        });
      } else if (parentMeeting) {
        // If this is a follow-up meeting, pre-fill some fields from parent
        form.setFieldsValue({
          title: `Follow-up: ${parentMeeting.title}`,
          description: `Follow-up meeting for: ${parentMeeting.title}`,
          location: parentMeeting.location,
          attendees: parentMeeting.attendees,
        });
      } else {
        form.resetFields();
      }
      
      // Reset follow-up form
      followUpForm.resetFields();
      setAddFollowUp(false);
    }
  }, [visible, editingMeeting, parentMeeting, form, followUpForm]);

  const handleFinish = (values) => {
    const meetingData = {
      ...values,
      scheduledAt: values.scheduledAt?.toISOString(),
      parentMeetingId: parentMeeting?._id, // Set parent meeting ID if this is a follow-up
    };

    // If follow-up is checked, prepare follow-up meeting data
    let followUpData = null;
    if (addFollowUp) {
      const followUpValues = followUpForm.getFieldsValue();
      followUpData = {
        title: followUpValues.title,
        description: followUpValues.description,
        scheduledAt: followUpValues.scheduledAt?.toISOString(),
        duration: followUpValues.duration,
        location: followUpValues.location,
        attendees: followUpValues.attendees,
        status: 'scheduled',
      };
    }

    onFinish(meetingData, followUpData);
  };

  const drawerTitle = editingMeeting 
    ? "Edit Meeting" 
    : parentMeeting 
      ? "Schedule Follow-up Meeting" 
      : "Schedule Meeting";

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      onClose={onClose}
      open={visible}
      width={500}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            {editingMeeting ? "Update" : "Schedule"}
          </Button>
        </div>
      }
    >
      {parentMeeting && (
        <Alert
          message="Creating follow-up meeting"
          description={`This is a follow-up to the meeting "${parentMeeting.title}" scheduled on ${dayjs(parentMeeting.scheduledAt).format('MMM DD, YYYY HH:mm')}`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          status: 'scheduled',
        }}
      >
        <Form.Item
          name="title"
          label="Meeting Title"
          rules={[
            { required: true, message: "Please enter meeting title" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (/^\s|\s$/.test(value)) {
                  return Promise.reject(new Error("No leading or trailing spaces allowed"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input 
            placeholder="Enter meeting title" 
            prefix={<CalendarOutlined style={{ color: '#bfbfbf' }} />} 
          />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea 
            rows={3} 
            placeholder="Enter meeting description, agenda, or notes" 
          />
        </Form.Item>

        <Form.Item
          name="scheduledAt"
          label="Scheduled Date & Time"
          rules={[
            {
              required: true,
              message: "Please select meeting date and time",
            },
          ]}
        >
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            className="w-full"
            placeholder="Select date and time"
          />
        </Form.Item>

        <Form.Item name="duration" label="Duration (minutes)">
          <InputNumber
            min={1}
            className="w-full"
            placeholder="Enter duration in minutes"
            prefix={<ClockCircleOutlined style={{ color: '#bfbfbf' }} />}
          />
        </Form.Item>

        <Form.Item name="location" label="Location">
          <Input 
            placeholder="Enter meeting location or video call link" 
            prefix={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />}
          />
        </Form.Item>

        <Form.Item name="attendees" label="Attendees">
          <Input placeholder="Enter attendees (comma separated)" />
        </Form.Item>

        <Form.Item name="status" label="Status" initialValue="scheduled">
          <Select>
            <Option value="scheduled">Scheduled</Option>
            <Option value="completed">Completed</Option>
            <Option value="cancelled">Cancelled</Option>
            <Option value="rescheduled">Rescheduled</Option>
          </Select>
        </Form.Item>

        {!editingMeeting && !parentMeeting && (
          <Form.Item>
            <Checkbox 
              checked={addFollowUp} 
              onChange={(e) => setAddFollowUp(e.target.checked)}
            >
              Schedule a follow-up meeting
            </Checkbox>
          </Form.Item>
        )}
      </Form>

      {addFollowUp && (
        <>
          <Divider orientation="left">Follow-up Meeting</Divider>
          <Form
            form={followUpForm}
            layout="vertical"
            initialValues={{
              status: 'scheduled',
            }}
          >
            <Form.Item
              name="title"
              label="Follow-up Title"
              rules={[{ required: true, message: "Please enter follow-up meeting title" }]}
            >
              <Input placeholder="Enter follow-up meeting title" />
            </Form.Item>

            <Form.Item name="description" label="Follow-up Description">
              <TextArea rows={2} placeholder="Enter follow-up meeting description" />
            </Form.Item>

            <Form.Item
              name="scheduledAt"
              label="Follow-up Date & Time"
              rules={[{ required: true, message: "Please select follow-up date and time" }]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                className="w-full"
                placeholder="Select follow-up date and time"
              />
            </Form.Item>

            <Form.Item name="duration" label="Duration (minutes)">
              <InputNumber
                min={1}
                className="w-full"
                placeholder="Enter duration in minutes"
              />
            </Form.Item>

            <Form.Item name="location" label="Location">
              <Input placeholder="Enter follow-up meeting location" />
            </Form.Item>

            <Form.Item name="attendees" label="Attendees">
              <Input placeholder="Enter attendees (comma separated)" />
            </Form.Item>
          </Form>
        </>
      )}
    </Drawer>
  );
};

export default MeetingDrawer;
